import { AppSettingType, FeatureManagementType, NonModelledElementsStatus, RebarEstimationStatus, SupplementalType } from "@api/apiClient";
import { CustomGridTreeDataGroupingCell } from "@components/DataGrid/components/cell/CustomGridTreeDataGroupingCell";
import { GRID_CHECKBOX_VISIBILITY_SELECTION_FIELD, GRID_VISIBILITY_COL_DEF } from "@components/DataGrid/components/colDef/GridVisibilityColDef";
import NullableBooleanColumnFilter from "@components/DataGrid/components/filters/NullableBooleanFilter";
import QuantificationGridHeader from "@components/DataGrid/components/headers/QuantificationGridHeader";
import { getSubSystemNameById, getSystemNameById } from "@features/analysis/utils/metadataEntityUtils";
import i18n from "@i18n/i18n";
import CheckIcon from "@mui/icons-material/Check";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import { Theme } from "@mui/material";
import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridApi,
  GridColDef,
  GridGroupNode,
  GridPreProcessEditCellProps,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import AppStore from "@stores/AppStore";
import _ from "lodash";
import { toJS } from "mobx";
import { MutableRefObject } from "react";
import { getDynamicColumns } from "../MuiAssignTab/MuiAssignGrid.dynamic-columns";

// TODO: the impactColumns need to be feature flagged based on ADStages
const defaultImpactColumns = ["totalCo2"];
const adStageImpactColumns = ["totalCo2", "a", "b", "c", "a1A2A3", "a4", "a5", "b1", "b2", "b3", "b4", "b5", "b6", "b7", "c1", "c2", "c3", "c4", "d", "biogenic"];

/**
 * Dynamically generates the column configuration for the Assign Grid.
 *
 * This function dynamically generates the columns configuration based
 * on application feature flags and project settings.
 *
 * @param apiRef - MutableRefObject to the GridApi for grid operations
 * @param scopeMap - Map of scope IDs to scope objects for scope column options
 * @param appStore - Application store containing feature flags and project settings
 * @param theme - MUI theme object for styling
 * @returns Array of GridColDef objects representing all grid columns
 */
export const getColumns = (apiRef: MutableRefObject<GridApi>, scopeMap: any, appStore: AppStore, theme: Theme, co2MaterialTypeSpecKind: string | null): GridColDef[] => {
  const columns = getDefaultColumns(apiRef, scopeMap, appStore, theme);

  const impactColumns = appStore.hasFeature(FeatureManagementType.AssignAllADStages) ? adStageImpactColumns : defaultImpactColumns;
  const impactColumnsToInsert = impactColumns.map((col) => getCo2eColumnDef(appStore, col, `element.${col}`, "", col));
  const rebarColumnsToInsert = impactColumns.map((col) => getCo2eColumnDef(appStore, col, `rebar.${col}`, "rebar", col));
  const nonModColumnsToInsert = impactColumns.map((col) => getCo2eColumnDef(appStore, col, `non-mod.${col}`, "nonMod", col));

  // Insert the impact columns starting at the position of the rebarMaterial column
  let index = columns.findIndex((col) => col.field === "nonModMaterial");
  columns.splice(index, 0, ...nonModColumnsToInsert);

  index = columns.findIndex((col) => col.field === "rebarMaterial");  
  columns.splice(index, 0, ...rebarColumnsToInsert);

  index = columns.findIndex((col) => col.field === "rebarPercentage");  
  columns.splice(index, 0, ...impactColumnsToInsert);
    
  // If we find Co2MaterialTypeSpecs in ModelExtendedData then add dynamic columns based on the Kind value
  const dynamicColumns = getDynamicColumns(co2MaterialTypeSpecKind);
  if (dynamicColumns.length > 0) {
    const index = columns.findIndex((col) => col.field === "cadMaterial");
    columns.splice(index, 0, ...dynamicColumns);
  }

  console.log("Assign Grid Columns:", columns);
  return columns;
};

const getDefaultColumns = (apiRef: MutableRefObject<GridApi>, scopeMap: any, appStore: AppStore, theme: Theme): GridColDef[] => [
  {
    ...GRID_VISIBILITY_COL_DEF,
    headerName: i18n.t("assign-grid.columns.visibility"),
  },
  {
    field: "assigned",
    headerName: i18n.t("assign-grid.columns.assigned"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.assigned")} />,
    flex: 1,
    type: "boolean",
    valueGetter: (_value: any, row: any) => {
      return row?.data?.assigned;
    },
    renderCell: (params) => {
      return params.value ? <ToggleOnIcon sx={{ color: theme?.palette.info.main }} /> : <ToggleOffIcon sx={{ color: theme?.palette.text.disabled }} />;
    },
    renderHeaderFilter: () => (
      <NullableBooleanColumnFilter
        apiRef={apiRef}
        field="assigned"
        labels={{
          none: i18n.t("assign-grid.columns.assigned-filter.none"),
          true: i18n.t("assign-grid.columns.assigned-filter.true"),
          false: i18n.t("assign-grid.columns.assigned-filter.false"),
        }}
      />
    ),
  },
 {
    field: "quantityTotal",
    headerName: i18n.t("assign-grid.columns.quantity-total"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.quantity-total")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => row?.data?.quantityTotal,
    valueFormatter: (value?: number) => {
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  },
  {
    field: "lengthTotal",
    headerName: i18n.t("quantification-header", { name: i18n.t("assign-grid.columns.length-total"), unit: i18n.t("units.m") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.length-total")} unit={i18n.t("units.m")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      // When An element contains insulation thickness as a child component
      // length should not be considered
      if (row.data?.cadName?.trim().includes("Insulation") && row.data?.lengthTotal == 0) {
        return;
      }
      return row?.data?.lengthTotal;
    },
    valueFormatter: (value: any) => {
      // alert(value)
      if (_.isFinite(value)) {
        return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
      } else {
        return "-";
      }
    },
  },
  {
    field: "areaTotal",
    headerName: i18n.t("quantification-header", { name: i18n.t("assign-grid.columns.area-total"), unit: i18n.t("units.m2") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.area-total")} unit={i18n.t("units.m2")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      // When An element contains insulation thickness as a child component
      // area should not be considered
      if (row.data?.cadName?.trim().includes("Insulation") && row.data?.areaTotal == 0) {
        return;
      }
      return row?.data?.areaTotal;
    },
    valueFormatter: (value: any) => {
      // alert(value)
      if (_.isFinite(value)) {
        return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
      } else {
        return "-";
      }
    },
  },
  {
    field: "volumeTotal",
    headerName: i18n.t("quantification-header", { name: i18n.t("assign-grid.columns.volume-total"), unit: i18n.t("units.m3") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.volume-total")} unit={i18n.t("units.m3")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => row?.data?.volumeTotal,
    valueFormatter: (value?: number) => {
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  },
{
    field: "systemId",
    headerName: i18n.t("assign-grid.columns.system"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.system")} />,
    flex: 1,
    align: "center",
    headerAlign: "center",
    valueGetter: (_val, row) => row?.data?.systemId || "",
    renderCell: (params) => {
      const value = params.row?.data?.systemId || "";
      const label = getSystemNameById(appStore.metadataEntities, value);
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            textAlign: "center",
          }}
        >
          {value ? label : ""}
        </div>
      );
    },
  },
  {
    field: "subSystemId",
    headerName: i18n.t("assign-grid.columns.sub-system"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.sub-system")} />,
    flex: 1,
    align: "center",
    headerAlign: "center",
    valueGetter: (_val, row) => row?.data?.subSystemId || "",
    renderCell: (params) => {
      const value = params.row?.data?.subSystemId || "";
      const label = getSubSystemNameById(appStore.metadataEntities, value);
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            textAlign: "center",
          }}
        >
          {value ? label : ""}
        </div>
      );
    },
  },
  {
    field: "attribute1",
    headerName: i18n.t("assign-grid.columns.attribute1"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.attribute1")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => row?.data?.attribute1 || "",
  },
  {
    field: "attribute2",
    headerName: i18n.t("assign-grid.columns.attribute2"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.attribute2")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => row?.data?.attribute2 || "",
  },
  {
    field: "attribute3",
    headerName: i18n.t("assign-grid.columns.attribute3"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.attribute3")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => row?.data?.attribute3 || "",
  },
 {
    field: "multiplierValue",
    headerName: i18n.t("assign-grid.columns.multiplier"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.multiplier")} />,
    width: 150,
    valueGetter: (_value: any, row: any) => {
      const value = row?.data?.multiplierValue;
      return typeof value === "number" && !isNaN(value) && value > 0 ? value.toFixed(2) : row?.data?.multiplierInput || "";
    },
  },  
  {
    field: "notes",
    headerName: i18n.t("assign-grid.columns.notes"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.notes")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => row?.data?.notes || "",
  },
  {
    field: "carbonImpactValue",
    headerName: i18n.t("assign-grid.columns.carbon-impact"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.carbon-impact")} />,
    width: 150,
    valueGetter: (_value: any, row: any) => {
      const value = row?.data?.carbonImpactValue;
      return typeof value === "number" && !isNaN(value) && value > 0 ? value.toFixed(2) : row?.data?.carbonImpactInput || "";
    },
  },
  {
    field: "carbonImpactNotes",
    headerName: i18n.t("assign-grid.columns.carbon-impact-notes"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.carbon-impact-notes")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => row?.data?.carbonImpactNotes || "",
  },
  {
    field: "cadMaterial",
    headerName: i18n.t("assign-grid.columns.cad-material"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.cad-material")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row?.data?.cadMaterial;
    },
  },
 {
    field: "material.co2MaterialType.name",
    headerName: i18n.t("assign-grid.columns.material-co2-material-type-name"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.material-co2-material-type-name")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      const name = row?.data?.supplementals?.find((s: any) => s.supplementalType === SupplementalType.Primary)?.material?.co2MaterialType?.name;
      if (name) {
        return name;
      }

      return row?.data?.material?.co2MaterialType?.name;
    },
  },
  {
    field: "material.co2MaterialType.a1A2A3",
    headerName: i18n.t("assign-grid.columns.material-co2-material-type-a1A2A3"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.material-co2-material-type-a1A2A3")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const name = row?.data?.supplementals?.find((s: any) => s.supplementalType === SupplementalType.Primary)?.material?.co2MaterialType?.a1A2A3;
      if (name) {
        return name;
      }

      return row?.data?.material?.co2MaterialType?.a1A2A3 ?? null;
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  },
  {
    field: "material.co2MaterialType.unit",
    headerName: i18n.t("assign-grid.columns.material-co2-material-type-unit"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.material-co2-material-type-unit")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      const name = row?.data?.supplementals?.find((s: any) => s.supplementalType === SupplementalType.Primary)?.material?.co2MaterialType?.unit;
      if (name) {
        return name;
      }

      return row?.data?.material?.co2MaterialType?.unit;
    },
  },

  /* A-D Impact Columns will be inserted here ... */

 {
    field: "rebarPercentage",
    headerName: i18n.t("quantification-header", { name: i18n.t("assign-grid.columns.rebar.percent"), unit: i18n.t("units.%") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.rebar.percent")} unit={i18n.t("units.%")} />,
    flex: 1,
    editable: true,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const editValue = appStore.rebarPercentEdits[row.key];
      if (editValue !== undefined) {
        return editValue;
      }

      return row.data?.supplementals?.find((s: any) => s.supplementalType === SupplementalType.Rebar)?.value;
    },
    valueFormatter: (value?: number) => {
      return i18n.t("percent", { value });
    },
    preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
      console.log("preProcessEditCellProps: ", params);

      let newValue = parseFloat(params.props.value);

      // check if newValue is a positive number
      if (_.isFinite(newValue)) {
        if (newValue >= 0 && newValue <= 100) {
          appStore.rebarPercentEdits[params.row.key!] = +newValue;
          appStore.rebarVolumeEdits[params.row.key!] = (+newValue * params.row.data.volumeTotal) / 100;
          // TODO: can we calculate CO2e here?
          //appStore.rebarCO2eEdits[params.row.key!] = (+newValue * params.row.data.rebarTotalCo2) / 100;
          console.log("rebarPercentEdits: ", toJS(appStore.rebarPercentEdits));
          console.log("rebarVolumeEdits: ", toJS(appStore.rebarVolumeEdits));
        } else {
          delete appStore.rebarVolumeEdits[params.row.key!];
          delete appStore.rebarPercentEdits[params.row.key!];
          delete appStore.rebarCO2eEdits[params.row.key!];
        }
      }
      return { ...params.props };
    },
  },
  {
    field: "rebarVolumeTotal",
    headerName: i18n.t("quantification-header", { name: i18n.t("assign-grid.columns.rebar.volume"), unit: i18n.t("units.m3") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.rebar.volume")} unit={i18n.t("units.m3")} />,
    flex: 1,
    editable: true,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const editValue = appStore.rebarVolumeEdits[row.key];
      if (editValue !== undefined) {
        return editValue;
      }

      return row?.data?.supplementals?.find((s: any) => s.supplementalType === SupplementalType.Rebar)?.volume ?? null;
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
    preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
      console.log("preProcessEditCellProps: ", params);

      let newValue = parseFloat(params.props.value);

      // check if newValue is a positive number
      if (_.isFinite(newValue)) {
        if (newValue >= 0 && newValue <= 100) {
          appStore.rebarVolumeEdits[params.row.key!] = +newValue;
          appStore.rebarPercentEdits[params.row.key!] = (100 * +newValue) / params.row.data.volumeTotal;
          // TODO: can we calculate CO2e here?
          //appStore.rebarCO2eEdits[params.row.key!] = (100 * +newValue) / params.row.data.volumeTotal;
          console.log("rebarVolumeEdits: ", toJS(appStore.rebarVolumeEdits));
          console.log("rebarPercentEdits: ", toJS(appStore.rebarPercentEdits));
        } else {
          delete appStore.rebarVolumeEdits[params.row.key!];
          delete appStore.rebarPercentEdits[params.row.key!];
          delete appStore.rebarCO2eEdits[params.row.key!];
        }
      }
      return { ...params.props };
    },
  },
  // insert rebar A-D impact columns here
  {
    field: "rebarMaterial",
    headerName: i18n.t("assign-grid.columns.rebar.material"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.rebar.material")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row?.data?.supplementals?.find((s: any) => s.supplementalType === SupplementalType.Rebar)?.material?.co2MaterialType?.name;
    },
  },
{
    field: "nonModPercentage",
    headerName: i18n.t("quantification-header", { name: i18n.t("assign-grid.columns.non-mod.percent"), unit: i18n.t("units.%") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.non-mod.percent")} unit={i18n.t("units.%")} />,
    flex: 1,
    editable: true,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const editValue = appStore.nonModelledPercentEdits[row.key];
      if (editValue !== undefined) {
        return editValue;
      }

      return row.data?.supplementals?.find((s: any) => s.supplementalType === SupplementalType.NonModelled)?.value;
    },
    valueFormatter: (value?: number) => {
      return i18n.t("percent", { value });
    },
    preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
      console.log("preProcessEditCellProps: ", params);

      let newValue = parseFloat(params.props.value);

      // check if newValue is a positive number
      if (_.isFinite(newValue)) {
        if (newValue >= 0 && newValue <= 100) {
          appStore.nonModelledPercentEdits[params.row.key!] = +newValue;
          appStore.nonModelledVolumeEdits[params.row.key!] = (+newValue * params.row.data.volumeTotal) / 100;
          appStore.nonModelledCO2eEdits[params.row.key!] = (+newValue * params.row.data.nonModTotalCo2) / 100;
          console.log("nonModelledPercentEdits: ", toJS(appStore.nonModelledPercentEdits));
          console.log("nonModelledVolumeEdits: ", toJS(appStore.nonModelledVolumeEdits));
        } else {
          delete appStore.nonModelledPercentEdits[params.row.key!];
          delete appStore.nonModelledVolumeEdits[params.row.key!];
          delete appStore.nonModelledCO2eEdits[params.row.key!];
        }
      }
      return { ...params.props };
    },
  },
  {
    field: "nonModVolumeTotal",
    headerName: i18n.t("quantification-header", { name: i18n.t("assign-grid.columns.non-mod.volume"), unit: i18n.t("units.m3") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.non-mod.volume")} unit={i18n.t("units.m3")} />,
    flex: 1,
    editable: true,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const editValue = appStore.nonModelledVolumeEdits[row.key];
      if (editValue !== undefined) {
        return editValue;
      }

      return row?.data?.supplementals?.find((s: any) => s.supplementalType === SupplementalType.NonModelled)?.volume ?? null;
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
    preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
      console.log("preProcessEditCellProps: ", params);

      let newValue = parseFloat(params.props.value);

      // check if newValue is a positive number
      if (_.isFinite(newValue)) {
        if (newValue >= 0 && newValue <= 100) {
          appStore.nonModelledVolumeEdits[params.row.key!] = +newValue;
          appStore.nonModelledPercentEdits[params.row.key!] = (100 * +newValue) / params.row.data.volumeTotal;
          // TODO: can we calculate CO2e here?
          //appStore.rebarCO2eEdits[params.row.key!] = (100 * +newValue) / params.row.data.volumeTotal;
          console.log("nonModelledVolumeEdits: ", toJS(appStore.nonModelledVolumeEdits));
          console.log("nonModelledPercentEdits: ", toJS(appStore.nonModelledPercentEdits));
        } else {
          delete appStore.nonModelledVolumeEdits[params.row.key!];
          delete appStore.nonModelledPercentEdits[params.row.key!];
          delete appStore.nonModelledCO2eEdits[params.row.key!];
        }
      }
      return { ...params.props };
    },
  },
  {
    field: "nonModMaterial",
    headerName: i18n.t("assign-grid.columns.non-mod.material"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.non-mod.material")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row?.data?.supplementals?.find((s: any) => s.supplementalType === SupplementalType.NonModelled)?.material?.co2MaterialType?.name;
    },
  },
  {
    field: "source",
    headerName: i18n.t("assign-grid.columns.data-source"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.data-source")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      const source = row?.data?.supplementals?.find((s: any) => s.supplementalType === SupplementalType.Primary)?.material?.co2MaterialType?.source;
      if (source) {
        return source;
      }

      return row?.data?.material?.co2MaterialType?.source;
    },
  },
  {
    field: "type",
    headerName: i18n.t("assign-grid.columns.type"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.type")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row?.type;
    },
  },
  {
    field: "key",
    headerName: i18n.t("assign-grid.columns.key"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.key")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row?.key;
    },
  },  
  {
    field: "cadId",
    headerName: i18n.t("assign-grid.columns.cad-id"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.cad-id")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => row?.data?.cadId || "",
  },
  {
    field: "cadModelCategory",
    headerName: i18n.t("assign-grid.columns.cad-model-category-name"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.cad-model-category-name")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row?.data?.cadModelCategory?.name;
    },
  },
  {
    field: "cadFamily",
    headerName: i18n.t("assign-grid.columns.cad-family"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.cad-family")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row?.data?.cadFamily;
    },
  },
  {
    field: "cadName",
    headerName: i18n.t("assign-grid.columns.cad-name"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.cad-name")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row?.data?.cadName;
    },
  },
  {
    field: "isStructuralMaterial",
    headerName: i18n.t("assign-grid.columns.is-structural-material"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.is-structural-material")} />,
    flex: 1,
    type: "boolean",
    renderCell: (params: GridRenderCellParams) => {
      return params.row.data?.isStructuralMaterial ? <CheckIcon sx={{ color: theme.palette.success.main }} /> : null;
    },
  },    
  {
    field: "siblings",
    headerName: i18n.t("assign-grid.columns.siblings"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.siblings")} />,
    flex: 0,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      if (row.data?.cadName?.trim().includes("Insulation")) {
        return;
      }
      return row?.data?.siblings;
    },
    valueFormatter: (value?: number) => {
      if (_.isFinite(value)) {
        return i18n.t("number", { value, maximumFractionDigits: 0 });
      } else {
        return "";
      }
    },
  },
  {
    field: "scopeId",
    headerName: i18n.t("assign-grid.columns.scope"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("assign-grid.columns.scope")} />,
    flex: 1,
    type: "singleSelect",
    valueGetter: (_value: any, row: any) => {
      return row.data?.scopeId?.toString() || "";
    },
    valueFormatter: (value?: string) => {
      return value ? scopeMap[value]?.name : "";
    },
    valueOptions: scopeMap ? [...Object.entries(scopeMap).map(([key, value]: [string, any]) => ({ value: key, label: value.name }))] : [],
  },  
];

const getCo2eColumnDef = (appStore: AppStore, column: string, field: string, prefix: string, prop: string): GridColDef => {
  switch (column) {
    case "a":
      return getACo2eColumnDef(field, prefix);
    case "b":
      return getBCo2eColumnDef(field, prefix);
    case "c":
      return getCCo2eColumnDef(field, prefix);
    case "d":
      return getDefaultCo2eColumnDef(field, prefix, prop);
    case "totalCo2":
      if (field === "rebar.totalCo2") {
        return getRebarTotalCo2eColumnDef(appStore, field);
      }
      if (field === "non-mod.totalCo2") {
        return getNonModTotalCo2eColumnDef(appStore, field);
      }
      return getDefaultCo2eColumnDef(field, prefix, prop);
    default:
      return getDefaultCo2eColumnDef(field, prefix, prop);
  }
};

const getRebarTotalCo2eColumnDef = (appStore: AppStore, field: string): GridColDef => {
  const rebarHeader = i18n.t("assign-grid.rebar.prefix", { defaultValue: "Rebar" });
  return {
    field: field,
    headerName: i18n.t("quantification-header", { name: rebarHeader, unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={rebarHeader} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const editValue = appStore.rebarCO2eEdits[row.key];
      if (editValue !== undefined) {
        return editValue;
      }

      return row?.data?.supplementals?.find((s: any) => s.supplementalType === SupplementalType.Rebar)?.material?.totalCo2 ?? null;
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

const getNonModTotalCo2eColumnDef = (appStore: AppStore, field: string): GridColDef => {
  const nonModelledHeader = i18n.t("assign-grid.nonmodelled.prefix", { defaultValue: "Non-Modelled" });
  return {
    field: field,
    headerName: i18n.t("quantification-header", { name: nonModelledHeader, unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={nonModelledHeader} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const editValue = appStore.nonModelledCO2eEdits[row.key];
      if (editValue !== undefined) {
        return editValue;
      }

      return row?.data?.supplementals?.find((s: any) => s.supplementalType === SupplementalType.NonModelled)?.material?.totalCo2 ?? null;
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

const getDefaultCo2eColumnDef = (field: string, prefix: string, prop: string): GridColDef => {
  return {
    field: field,
    headerName: i18n.t("quantification-header", { name: i18n.t(`assign-grid.columns.${field}`), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t(`assign-grid.columns.${field}`)} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      if (prefix) {
        return row?.data?.[prefix]?.co2Totals?.[prop] ?? null;
      } else {
        return row?.data?.co2Totals?.[prop] ?? null;
      }
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

const getACo2eColumnDef = (field: string, prefix: string): GridColDef => {
  return {
    field: field,
    headerName: i18n.t("quantification-header", { name: i18n.t(`assign-grid.columns.${field}`), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t(`assign-grid.columns.${field}`)} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const [a1a2a3, a4, a5] = (() => {
        if (prefix) {
          return [row?.data?.[prefix]?.co2Totals?.a1A2A3 ?? null, row?.data?.[prefix]?.co2Totals?.a4 ?? null, row?.data?.[prefix]?.co2Totals?.a5 ?? null];
        } else {
          return [row?.data?.co2Totals?.a1A2A3 ?? null, row?.data?.co2Totals?.a4 ?? null, row?.data?.co2Totals?.a5 ?? null];
        }
      })();

      if (a1a2a3 == null && a4 == null && a5 == null) {
        return null;
      }

      return (a1a2a3 || 0) + (a4 || 0) + (a5 || 0);
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

const getBCo2eColumnDef = (field: string, prefix: string): GridColDef => {
  return {
    field: field,
    headerName: i18n.t("quantification-header", { name: i18n.t(`assign-grid.columns.${field}`), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t(`assign-grid.columns.${field}`)} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const [b1, b2, b3, b4, b5, b6, b7] = (() => {
        if (prefix) {
          return [
            row?.data?.[prefix]?.co2Totals?.b1 ?? null,
            row?.data?.[prefix]?.co2Totals?.b2 ?? null,
            row?.data?.[prefix]?.co2Totals?.b3 ?? null,
            row?.data?.[prefix]?.co2Totals?.b4 ?? null,
            row?.data?.[prefix]?.co2Totals?.b5 ?? null,
            row?.data?.[prefix]?.co2Totals?.b6 ?? null,
            row?.data?.[prefix]?.co2Totals?.b7 ?? null,
          ];
        } else {
          return [
            row?.data?.co2Totals?.b1 ?? null,
            row?.data?.co2Totals?.b2 ?? null,
            row?.data?.co2Totals?.b3 ?? null,
            row?.data?.co2Totals?.b4 ?? null,
            row?.data?.co2Totals?.b5 ?? null,
            row?.data?.co2Totals?.b6 ?? null,
            row?.data?.co2Totals?.b7 ?? null,
          ];
        }
      })();

      if (b1 == null && b2 == null && b3 == null && b4 == null && b5 == null && b6 == null && b7 == null) {
        return null;
      }

      return (b1 || 0) + (b2 || 0) + (b3 || 0) + (b4 || 0) + (b5 || 0) + (b6 || 0) + (b7 || 0);
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

const getCCo2eColumnDef = (field: string, prefix: string): GridColDef => {
  return {
    field: field,
    headerName: i18n.t("quantification-header", { name: i18n.t(`assign-grid.columns.${field}`), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t(`assign-grid.columns.${field}`)} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const [c1, c2, c3, c4] = (() => {
        if (prefix) {
          return [
            row?.data?.[prefix]?.co2Totals?.c1 ?? null,
            row?.data?.[prefix]?.co2Totals?.c2 ?? null,
            row?.data?.[prefix]?.co2Totals?.c3 ?? null,
            row?.data?.[prefix]?.co2Totals?.c4 ?? null,
          ];
        } else {
          return [row?.data?.co2Totals?.c1 ?? null, row?.data?.co2Totals?.c2 ?? null, row?.data?.co2Totals?.c3 ?? null, row?.data?.co2Totals?.c4 ?? null];
        }
      })();

      if (c1 == null && c2 == null && c3 == null && c4 == null) {
        return null;
      }

      return (c1 || 0) + (c2 || 0) + (c3 || 0) + (c4 || 0);
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  };
};

export const getGroupingColumnDef = (headerName: string): GridColDef => {
  return {
    field: "name",
    headerName: headerName,
    renderHeader: () => <QuantificationGridHeader name={headerName} />,
    filterable: true,
    hideable: false,
    valueGetter: (_value: any, row: any) => row?.data?.label || "",
    renderCell: (params) => {
      return <CustomGridTreeDataGroupingCell {...(params as GridRenderCellParams<any, any, any, GridGroupNode>)} hideDescendantCount={true} />;
    },
  };
};

const generateColumns = (srcArr: string[], addArr: string[], prefix: string, insertAfter: string): string[] => {
  const newArr = [];
  const insertIndex = srcArr.indexOf(insertAfter);
  if (insertIndex === -1) {
    newArr.push(...srcArr);
    newArr.push(...addArr.map((col) => `${prefix}${col}`));
  } else {
    for (let i = 0; i < srcArr.length; i++) {
      if (i === insertIndex) {
        newArr.push(srcArr[i]);
        newArr.push(...addArr.map((col) => `${prefix}${col}`));
      } else {
        newArr.push(srcArr[i]);
      }
    }
  }
  return newArr;
};

export const getFilteredColumns = (columns: GridColDef[], appStore: any, profileLevelRebarSettings:boolean=false): { filteredColumns: GridColDef[]; columnVisibilityModel: Record<string, boolean> } => {

  let filteredColumns = [...columns];
  let defaultVisibleColumns = [
    GRID_CHECKBOX_SELECTION_FIELD,
    GRID_TREE_DATA_GROUPING_FIELD,

    "label",
    GRID_CHECKBOX_VISIBILITY_SELECTION_FIELD,
    "quantityTotal",
    "lengthTotal",
    "areaTotal",
    "volumeTotal",
    "systemId",
    "subSystemId",
    "attribute1",
    "attribute2",
    "attribute3",
    "cadMaterial",
    "material.co2MaterialType.name",
    "material.co2MaterialType.a1A2A3",
    "material.co2MaterialType.unit",
    "element.totalCo2",
    "element.a",
    "element.b",
    "element.c",
    "element.a1A2A3",
    "element.a4",
    "element.a5",
    "element.b1",
    "element.b2",
    "element.b3",
    "element.b4",
    "element.b5",
    "element.b6",
    "element.b7",
    "element.c1",
    "element.c2",
    "element.c3",
    "element.c4",
    "element.d",
    "element.biogenic",
    "rebarPercentage",
    "rebarVolumeTotal",
    "rebar.totalCo2",
    "rebarMaterial",
    "nonModPercentage",
    "nonModVolumeTotal",
    "non-mod.biogenic",
    "non-mod.totalCo2",
    "nonModMaterial" 
  ];
  const impactColumns = appStore.hasFeature(FeatureManagementType.AssignAllADStages) ? adStageImpactColumns : defaultImpactColumns;
  const rebarColumns = generateColumns(["rebarPercentage", "rebarVolumeTotal", "rebarMaterial"], impactColumns, "rebar.", "rebarVolumeTotal");
  const nonModColumns = generateColumns(["nonModPercentage", "nonModVolumeTotal", "nonModMaterial"], impactColumns, "non-mod.", "nonModVolumeTotal");
  const scopeColumns = ["scopeId", "scoped"];

  const enableRebarLineItems = appStore.hasFeature(FeatureManagementType.EnableRebarLineItems);
  const enableNonModelledLineItems = appStore.hasFeature(FeatureManagementType.EnableNonModelledLineItems);

  // TODO: ADStages needs to be more granular, but for now we just check if the feature is enabled
  if (appStore.hasFeature(FeatureManagementType.AssignAllADStages)) {
    defaultVisibleColumns = generateColumns(defaultVisibleColumns, impactColumns, "element.", "notes");
  }
  if (appStore.project?.nonModelledElementsStatus !== NonModelledElementsStatus.Disabled) {
    defaultVisibleColumns = generateColumns(defaultVisibleColumns, rebarColumns, "non-mod.", "notes");
  }
  if (!appStore.hasFeature(FeatureManagementType.AttributesVisibility)) {
    filteredColumns = filteredColumns.filter((c) => c.field !== "systemId" && c.field !== "subSystemId");
  }
  // Handle Rebar columns (only if feature flag is OFF)
  if (!enableRebarLineItems) {
     if (appStore.project?.rebarEstimationStatus !== RebarEstimationStatus.Disabled || profileLevelRebarSettings) {
      defaultVisibleColumns = generateColumns(defaultVisibleColumns, rebarColumns, "rebar.", "volumeTotal");
     }

    // Keep rebarPercentage ALWAYS, remove other rebar columns only when disabled
    const rebarFieldsToRemove = rebarColumns.filter((f) => f !== "rebarPercentage");

    if (appStore.project?.rebarEstimationStatus === RebarEstimationStatus.Disabled && !profileLevelRebarSettings) {
       filteredColumns = filteredColumns.filter((c) => !rebarFieldsToRemove.includes(c.field));
    }
  } else {
    // If line items are enabled, keep the summary total column for easy comparison.
    const rebarFieldsToRemove = rebarColumns.filter((f) => f !== "rebarPercentage" && f !== "rebar.totalCo2");
    filteredColumns = filteredColumns.filter((c) => !rebarFieldsToRemove.includes(c.field));
  }

  // Handle NonModelled columns (only if feature flag is OFF)
  if (!enableNonModelledLineItems) {
    if (appStore.project?.nonModelledElementsStatus !== NonModelledElementsStatus.Disabled) {
      defaultVisibleColumns = generateColumns(defaultVisibleColumns, nonModColumns, "non-mod.", "notes");
    }
  } else {
    // If line items are enabled, keep the summary total column for easy comparison.
    const nonModFieldsToRemove = nonModColumns.filter((f) => f !== "non-mod.totalCo2");
    filteredColumns = filteredColumns.filter((c) => !nonModFieldsToRemove.includes(c.field));
  }

  if (appStore.project?.nonModelledElementsStatus === NonModelledElementsStatus.Disabled) {
    filteredColumns = filteredColumns.filter((c) => !nonModColumns.includes(c.field));
  }
  if (!appStore.hasFeature(FeatureManagementType.ModelScopes)) {
    filteredColumns = filteredColumns.filter((c) => !scopeColumns.includes(c.field));
  }

  // Generate column visibility model
  const columnVisibilityModel = filteredColumns.reduce<Record<string, boolean>>((acc, col) => {
    acc[col.field] = defaultVisibleColumns.includes(col.field); // Set true for defaultColumns, false for others
    return acc;
  }, {});

  return { filteredColumns, columnVisibilityModel };
};
